/*
 * Qiyuan Garden Lamps
 * Places remembrance lamps inside the 3D halls of the Eastern Memorial Garden.
 *
 * Works with qiyuan-3d-viewer-v2.js, which exposes `element.qiyuanViewer`
 * ({ three, scene, camera, controls, canvas, stage, onFrame, getModel }).
 * The garden controller (qiyuan-3d-core-entrances.js) drives this module:
 *   lamps.showScene(scene, model)   after a hall model is ready
 *   lamps.hide()                    while a new model loads
 *   lamps.setLights(scene, lights)  active public lights from /apps/qiyuan-lights
 *   lamps.preview(scene, details)   light the visitor's own (unpaid) preview lamp
 *   lamps.clearPreview()
 *
 * Coordinates below are in the viewer's normalised world space (the viewer
 * centres every model and scales its largest side to 2.05 units).
 */
(function (global) {
  'use strict';

  if (global.QiyuanGardenLamps) return;

  var DOUBLE_SIDE = 2; // THREE.DoubleSide
  var LAMP_SCALE = 0.066;
  var VIEW_ELEVATION = 0.36; // radians above the horizon for the hall camera
  var STONE_TILE = 0.62;     // world units covered by one bluestone texture tile
  var MAX_LIT = 24;

  function grid(xs, zs) {
    var slots = [];
    zs.forEach(function (z) {
      xs.forEach(function (x) {
        slots.push([x, z]);
      });
    });
    return slots;
  }

  // Slots are ordered by fill priority: closest to the shrine / path first.
  function byPriority(slots, focus) {
    return slots.slice().sort(function (a, b) {
      var da = Math.abs(a[0] - focus[0]) * 0.6 + Math.abs(a[1] - focus[1]);
      var db = Math.abs(b[0] - focus[0]) * 0.6 + Math.abs(b[1] - focus[1]);
      return da - db || a[0] - b[0];
    });
  }

  var LAYOUTS = {
    dizang: {
      slots: byPriority(grid([-0.47, -0.37, -0.27, 0.27, 0.37, 0.47], [0.1, 0.24, 0.38, 0.52]), [0, 0.1]),
      lightAt: [0, 0.3],
    },
    guanyin: {
      slots: byPriority(grid([-0.48, -0.38, -0.28, 0.28, 0.38, 0.48], [0.3, 0.43, 0.56, 0.69]), [0, 0.3]),
      lightAt: [0, 0.48],
    },
    pet: {
      slots: byPriority(
        grid([0.3, 0.41, 0.52, 0.63], [0.3, 0.44, 0.58, 0.72]).concat(grid([-0.74, -0.63], [0.3, 0.44, 0.58, 0.72])),
        [0.3, 0.3]
      ),
      lightAt: [0.35, 0.5],
    },
    // Confucian and Daoist halls use bronze oil lamps (长明灯) instead of lotus lamps.
    ancestral: {
      style: 'oil',
      slots: byPriority(grid([-0.48, -0.38, -0.28, 0.28, 0.38, 0.48], [0.2, 0.34, 0.48, 0.62]), [0, 0.2]),
      lightAt: [0, 0.4],
    },
    taiji: {
      style: 'oil',
      slots: byPriority(grid([-0.47, -0.37, -0.27, 0.27, 0.37, 0.47], [0.1, 0.24, 0.38, 0.52]), [0, 0.1]),
      lightAt: [0, 0.3],
    },
  };

  function clean(value) {
    return typeof value === 'string' ? value.trim() : '';
  }

  function reducedMotion() {
    return Boolean(global.matchMedia && global.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  /* ---------- geometry ---------- */

  // Revolve a profile around Y. `shape(angle, t)` may return {r, y} multipliers.
  function revolve(T, profile, segments, shape) {
    var positions = [];
    var uvs = [];
    var index = [];
    var rows = profile.length;
    var i;
    var j;
    for (i = 0; i < rows; i += 1) {
      var t = rows > 1 ? i / (rows - 1) : 0;
      for (j = 0; j <= segments; j += 1) {
        var angle = (j / segments) * Math.PI * 2;
        var r = profile[i][0];
        var y = profile[i][1];
        if (shape) {
          var mod = shape(angle, t);
          r *= mod.r;
          y += mod.y;
        }
        positions.push(Math.cos(angle) * r, y, Math.sin(angle) * r);
        uvs.push(j / segments, t);
      }
    }
    for (i = 0; i < rows - 1; i += 1) {
      for (j = 0; j < segments; j += 1) {
        var a = i * (segments + 1) + j;
        var b = a + segments + 1;
        index.push(a, b, a + 1, b, b + 1, a + 1);
      }
    }
    var geometry = new T.BufferGeometry();
    geometry.setAttribute('position', new T.Float32BufferAttribute(positions, 3));
    geometry.setAttribute('uv', new T.Float32BufferAttribute(uvs, 2));
    geometry.setIndex(index);
    geometry.computeVertexNormals();
    return geometry;
  }

  // Three rings of pointed, cupped petals. Vertex colours darken the petal base for depth.
  function lotusGeometry(T) {
    var layers = [
      { count: 8, phase: 0, r0: 0.1, length: 0.58, width: 0.21, a0: 0.26, a1: 0.95, cup: 0.34, y0: 0.0, shade: 0.72 },
      { count: 8, phase: Math.PI / 8, r0: 0.08, length: 0.5, width: 0.18, a0: 0.62, a1: 1.22, cup: 0.38, y0: 0.03, shade: 0.8 },
      { count: 6, phase: 0.3, r0: 0.05, length: 0.36, width: 0.14, a0: 1.0, a1: 1.42, cup: 0.42, y0: 0.06, shade: 0.88 },
    ];
    var NS = 9;
    var NW = 6;
    var positions = [];
    var colors = [];
    var index = [];
    layers.forEach(function (layer) {
      for (var k = 0; k < layer.count; k += 1) {
        var phi = layer.phase + (k / layer.count) * Math.PI * 2;
        var c = Math.cos(phi);
        var sn = Math.sin(phi);
        var rho = layer.r0;
        var y = layer.y0;
        var centre = [];
        var i;
        var j;
        for (i = 0; i <= NS; i += 1) {
          var theta = layer.a0 + (layer.a1 - layer.a0) * (i / NS);
          if (i > 0) {
            rho += Math.cos(theta) * layer.length / NS;
            y += Math.sin(theta) * layer.length / NS;
          }
          centre.push([rho, y, theta]);
        }
        var start = positions.length / 3;
        for (i = 0; i <= NS; i += 1) {
          var t = i / NS;
          var half = layer.width * Math.pow(Math.sin(Math.PI * Math.min(0.999, 0.12 + 0.88 * t)), 0.75);
          for (j = 0; j <= NW; j += 1) {
            var w = (j / NW) * 2 - 1;
            var lift = layer.cup * half * w * w;
            var pr = centre[i][0] - Math.sin(centre[i][2]) * lift;
            var py = centre[i][1] + Math.cos(centre[i][2]) * lift;
            var lateral = w * half;
            positions.push(pr * c - lateral * sn, py, pr * sn + lateral * c);
            var shade = layer.shade + (1 - layer.shade) * Math.pow(t, 0.8);
            colors.push(shade, shade * (0.96 + 0.04 * t), shade * (0.9 + 0.1 * t));
          }
        }
        for (i = 0; i < NS; i += 1) {
          for (j = 0; j < NW; j += 1) {
            var a = start + i * (NW + 1) + j;
            var b = a + NW + 1;
            index.push(a, b, a + 1, a + 1, b, b + 1);
          }
        }
      }
    });
    var geometry = new T.BufferGeometry();
    geometry.setAttribute('position', new T.Float32BufferAttribute(positions, 3));
    geometry.setAttribute('color', new T.Float32BufferAttribute(colors, 3));
    geometry.setIndex(index);
    geometry.computeVertexNormals();
    return geometry;
  }

  function buildGeometries(T) {
    return {
      lotus: lotusGeometry(T),
      heart: revolve(T, [[0.0, 0.0], [0.1, 0.0], [0.12, 0.06], [0.1, 0.11], [0.0, 0.12]], 24),
      stand: revolve(T, [[0.0, 0.0], [0.3, 0.0], [0.33, 0.025], [0.32, 0.05], [0.24, 0.07], [0.15, 0.11],
        [0.13, 0.14], [0.22, 0.17], [0.26, 0.2], [0.0, 0.2]], 40),
      oilBody: revolve(T, [[0.0, 0.0], [0.42, 0.0], [0.44, 0.05], [0.3, 0.1], [0.12, 0.17], [0.08, 0.3], [0.07, 0.55],
        [0.11, 0.62], [0.3, 0.66], [0.46, 0.74], [0.5, 0.8], [0.46, 0.81]], 40),
      oilSurface: revolve(T, [[0.0, 0.76], [0.44, 0.76]], 40),
      flame: new T.PlaneGeometry(0.42, 0.8),
      core: new T.PlaneGeometry(0.2, 0.38),
      halo: new T.PlaneGeometry(4.2, 4.2),
      pool: new T.CircleGeometry(2.2, 40),
    };
  }

  /* ---------- textures ---------- */

  function canvasTexture(T, width, height, draw) {
    var canvas = global.document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    draw(canvas.getContext('2d'), width, height);
    var texture = new T.Texture(canvas);
    texture.colorSpace = 'srgb';
    texture.needsUpdate = true;
    return texture;
  }

  function buildTextures(T) {
    return {
      flame: canvasTexture(T, 64, 128, function (ctx, w, h) {
        var g = ctx.createRadialGradient(w / 2, h * 0.7, 2, w / 2, h * 0.62, h * 0.48);
        g.addColorStop(0, 'rgba(255,244,205,0.95)');
        g.addColorStop(0.3, 'rgba(255,190,80,0.9)');
        g.addColorStop(0.65, 'rgba(240,110,30,0.55)');
        g.addColorStop(1, 'rgba(200,70,20,0)');
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.moveTo(w / 2, h * 0.02);
        ctx.bezierCurveTo(w * 0.62, h * 0.3, w * 0.94, h * 0.62, w / 2, h * 0.96);
        ctx.bezierCurveTo(w * 0.06, h * 0.62, w * 0.38, h * 0.3, w / 2, h * 0.02);
        ctx.fill();
        var b = ctx.createRadialGradient(w / 2, h * 0.9, 1, w / 2, h * 0.9, w * 0.3);
        b.addColorStop(0, 'rgba(120,150,255,0.55)');
        b.addColorStop(1, 'rgba(120,150,255,0)');
        ctx.fillStyle = b;
        ctx.fillRect(0, h * 0.7, w, h * 0.3);
      }),
      core: canvasTexture(T, 32, 64, function (ctx, w, h) {
        var g = ctx.createRadialGradient(w / 2, h * 0.68, 1, w / 2, h * 0.6, h * 0.45);
        g.addColorStop(0, 'rgba(255,255,248,1)');
        g.addColorStop(0.45, 'rgba(255,240,190,0.85)');
        g.addColorStop(1, 'rgba(255,220,140,0)');
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.ellipse(w / 2, h * 0.6, w * 0.36, h * 0.38, 0, 0, Math.PI * 2);
        ctx.fill();
      }),
      halo: canvasTexture(T, 128, 128, function (ctx, w, h) {
        var g = ctx.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, w / 2);
        g.addColorStop(0, 'rgba(255,200,110,0.75)');
        g.addColorStop(0.2, 'rgba(255,170,70,0.35)');
        g.addColorStop(0.55, 'rgba(255,140,50,0.08)');
        g.addColorStop(1, 'rgba(255,130,40,0)');
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, w, h);
      }),
      pool: canvasTexture(T, 128, 128, function (ctx, w, h) {
        var g = ctx.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, w / 2);
        g.addColorStop(0, 'rgba(255,170,80,0.55)');
        g.addColorStop(0.45, 'rgba(255,160,70,0.2)');
        g.addColorStop(1, 'rgba(255,150,60,0)');
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, w, h);
      }),
    };
  }

  /* ---------- bluestone (青石) paving ---------- */

  function seeded(seed) {
    var v = seed;
    return function () {
      v = (v * 16807) % 2147483647;
      return (v - 1) / 2147483646;
    };
  }

  // Tileable flagstones in running bond: colour map + normal map (grooved joints, softened edges).
  function bluestoneTextures(T, renderer) {
    var size = 1024;
    var rows = 8;
    var rowH = size / rows;
    var rnd = seeded(20261014);
    var slabs = [];
    for (var r = 0; r < rows; r += 1) {
      var x = -Math.floor(rnd() * 180);
      while (x < size) {
        var len = 150 + Math.floor(rnd() * 170);
        if (x + len > size && x + len - size < 60) len = size - x;  // avoid slivers at the seam
        slabs.push({ x: x, y: r * rowH, w: len, h: rowH, tone: rnd() });
        x += len;
      }
    }
    var colorCanvas = global.document.createElement('canvas');
    colorCanvas.width = colorCanvas.height = size;
    var ctx = colorCanvas.getContext('2d');
    ctx.fillStyle = 'rgb(54,60,64)';
    ctx.fillRect(0, 0, size, size);
    var heightCanvas = global.document.createElement('canvas');
    heightCanvas.width = heightCanvas.height = size / 2;
    var hctx = heightCanvas.getContext('2d');
    hctx.fillStyle = '#000';
    hctx.fillRect(0, 0, size / 2, size / 2);
    slabs.forEach(function (slab) {
      [-size, 0, size].forEach(function (shift) {
        var x0 = slab.x + shift;
        if (x0 + slab.w < 0 || x0 > size) return;
        var base = 98 + slab.tone * 30;
        ctx.fillStyle = 'rgb(' + Math.round(base - 9) + ',' + Math.round(base + 2) + ',' + Math.round(base + 10) + ')';
        ctx.fillRect(x0 + 2, slab.y + 2, slab.w - 4, slab.h - 4);
        // Weathered, slightly darker slab edges.
        ctx.strokeStyle = 'rgba(30,36,40,0.16)';
        ctx.lineWidth = 6;
        ctx.strokeRect(x0 + 5, slab.y + 5, slab.w - 10, slab.h - 10);
        hctx.fillStyle = '#fff';
        hctx.fillRect((x0 + 4) / 2, (slab.y + 4) / 2, (slab.w - 8) / 2, (slab.h - 8) / 2);
      });
    });
    // Mottling and grain.
    var img = ctx.getImageData(0, 0, size, size);
    var d = img.data;
    var coarse = [];
    for (var i = 0; i < 17 * 17; i += 1) coarse.push(rnd() * 2 - 1);
    for (var py = 0; py < size; py += 1) {
      var gy = (py / size) * 16;
      var y0 = Math.floor(gy);
      var fy = gy - y0;
      for (var px = 0; px < size; px += 1) {
        var gx = (px / size) * 16;
        var x0 = Math.floor(gx);
        var fx = gx - x0;
        var m = coarse[y0 * 17 + x0] * (1 - fx) * (1 - fy) + coarse[y0 * 17 + x0 + 1] * fx * (1 - fy) +
          coarse[(y0 + 1) * 17 + x0] * (1 - fx) * fy + coarse[(y0 + 1) * 17 + x0 + 1] * fx * fy;
        var n = (rnd() - 0.5) * 16 + m * 11 + (rnd() < 0.01 ? -20 : 0);
        var k = (py * size + px) * 4;
        d[k] = Math.max(0, Math.min(255, d[k] + n));
        d[k + 1] = Math.max(0, Math.min(255, d[k + 1] + n));
        d[k + 2] = Math.max(0, Math.min(255, d[k + 2] + n));
      }
    }
    ctx.putImageData(img, 0, 0);

    // Normal map from the blurred slab height mask.
    var hs = size / 2;
    var hdata = hctx.getImageData(0, 0, hs, hs).data;
    var height = new Float32Array(hs * hs);
    for (var q = 0; q < hs * hs; q += 1) height[q] = hdata[q * 4] / 255;
    function blur(src) {
      var out = new Float32Array(hs * hs);
      for (var yy = 0; yy < hs; yy += 1) {
        for (var xx = 0; xx < hs; xx += 1) {
          var sum = 0;
          for (var o = -2; o <= 2; o += 1) sum += src[yy * hs + ((xx + o + hs) % hs)];
          out[yy * hs + xx] = sum / 5;
        }
      }
      var out2 = new Float32Array(hs * hs);
      for (var y2 = 0; y2 < hs; y2 += 1) {
        for (var x2 = 0; x2 < hs; x2 += 1) {
          var sum2 = 0;
          for (var o2 = -2; o2 <= 2; o2 += 1) sum2 += out[((y2 + o2 + hs) % hs) * hs + x2];
          out2[y2 * hs + x2] = sum2 / 5;
        }
      }
      return out2;
    }
    height = blur(height);
    var normalCanvas = global.document.createElement('canvas');
    normalCanvas.width = normalCanvas.height = hs;
    var nctx = normalCanvas.getContext('2d');
    var nimg = nctx.createImageData(hs, hs);
    var nd = nimg.data;
    var strength = 2.2;
    for (var ny = 0; ny < hs; ny += 1) {
      for (var nx = 0; nx < hs; nx += 1) {
        var l = height[ny * hs + ((nx - 1 + hs) % hs)];
        var rr = height[ny * hs + ((nx + 1) % hs)];
        var u = height[((ny - 1 + hs) % hs) * hs + nx];
        var dn = height[((ny + 1) % hs) * hs + nx];
        var vx = (l - rr) * strength;
        var vy = (dn - u) * strength;
        var len2 = Math.sqrt(vx * vx + vy * vy + 1);
        var kk = (ny * hs + nx) * 4;
        nd[kk] = Math.round((vx / len2 * 0.5 + 0.5) * 255);
        nd[kk + 1] = Math.round((vy / len2 * 0.5 + 0.5) * 255);
        nd[kk + 2] = Math.round((1 / len2 * 0.5 + 0.5) * 255);
        nd[kk + 3] = 255;
      }
    }
    nctx.putImageData(nimg, 0, 0);

    var anisotropy = renderer && renderer.capabilities && renderer.capabilities.getMaxAnisotropy
      ? Math.min(8, renderer.capabilities.getMaxAnisotropy()) : 1;
    var map = new T.Texture(colorCanvas);
    map.colorSpace = 'srgb';
    var normalMap = new T.Texture(normalCanvas);
    normalMap.colorSpace = '';
    [map, normalMap].forEach(function (texture) {
      texture.wrapS = texture.wrapT = 1000; // RepeatWrapping
      texture.anisotropy = anisotropy;
      texture.needsUpdate = true;
    });
    return { map: map, normalMap: normalMap };
  }

  /* ---------- ground sampling ---------- */

  function groundMesh(T, model) {
    var best = null;
    var bestArea = 0;
    model.traverse(function (node) {
      if (!node.isMesh) return;
      var box = new T.Box3().setFromObject(node);
      var area = (box.max.x - box.min.x) * (box.max.z - box.min.z);
      if (area > bestArea) {
        bestArea = area;
        best = node;
      }
    });
    return best;
  }

  function groundSampler(T, model) {
    model.updateWorldMatrix(true, true);
    var mesh = groundMesh(T, model);
    var fallback = mesh ? new T.Box3().setFromObject(mesh).max.y : 0;
    if (!mesh || !mesh.geometry || !mesh.geometry.attributes.position) {
      return function () {
        return fallback;
      };
    }
    mesh.updateMatrixWorld(true);
    var position = mesh.geometry.attributes.position;
    var index = mesh.geometry.index;
    var count = index ? index.count : position.count;
    var world = [];
    var v = new T.Vector3();
    for (var i = 0; i < position.count; i += 1) {
      v.set(position.getX(i), position.getY(i), position.getZ(i)).applyMatrix4(mesh.matrixWorld);
      world.push(v.x, v.y, v.z);
    }
    function vertex(n) {
      var k = (index ? index.getX(n) : n) * 3;
      return [world[k], world[k + 1], world[k + 2]];
    }
    return function (x, z) {
      var best = -Infinity;
      for (var n = 0; n + 2 < count; n += 3) {
        var a = vertex(n);
        var b = vertex(n + 1);
        var c = vertex(n + 2);
        var d = (b[2] - c[2]) * (a[0] - c[0]) + (c[0] - b[0]) * (a[2] - c[2]);
        if (Math.abs(d) < 1e-9) continue;
        var l1 = ((b[2] - c[2]) * (x - c[0]) + (c[0] - b[0]) * (z - c[2])) / d;
        var l2 = ((c[2] - a[2]) * (x - c[0]) + (a[0] - c[0]) * (z - c[2])) / d;
        var l3 = 1 - l1 - l2;
        if (l1 < -1e-6 || l2 < -1e-6 || l3 < -1e-6) continue;
        var y = l1 * a[1] + l2 * b[1] + l3 * c[1];
        if (y > best) best = y;
      }
      return best === -Infinity ? fallback : best;
    };
  }

  /* ---------- lamps ---------- */

  function Lamps(viewerElement, options) {
    this.viewerElement = viewerElement;
    this.options = options || {};
    this.api = null;
    this.scene = '';
    this.model = null;
    this.group = null;
    this.lamps = [];
    this.lightsByScene = {};
    this.previewDetails = null;
    this.previewScene = '';
    this.tapLabel = null;
    this.tapTimer = null;
    this.frame = this.frame.bind(this);
    this.handlePointerDown = this.handlePointerDown.bind(this);
    this.handlePointerUp = this.handlePointerUp.bind(this);
  }

  Lamps.prototype.ensure = function () {
    if (this.api) return true;
    var api = this.viewerElement && this.viewerElement.qiyuanViewer;
    if (!api || !api.three || !api.three.Float32BufferAttribute) return false;
    var T = api.three;
    this.api = api;
    this.T = T;
    this.geometries = buildGeometries(T);
    this.textures = buildTextures(T);
    this.materials = {
      // Amber crystal when lit, white jade when waiting to be lit (matches the site's lotus lamp imagery).
      lotusLit: new T.MeshStandardMaterial({ color: 0xffb550, vertexColors: true, metalness: 0.05, roughness: 0.26, emissive: 0xff7a18, emissiveIntensity: 0.82, side: DOUBLE_SIDE }),
      lotusUnlit: new T.MeshStandardMaterial({ color: 0xf3eee4, vertexColors: true, metalness: 0.0, roughness: 0.38, side: DOUBLE_SIDE }),
      heartLit: new T.MeshStandardMaterial({ color: 0xffe2a0, roughness: 0.3, emissive: 0xffb24a, emissiveIntensity: 1.3 }),
      heartUnlit: new T.MeshStandardMaterial({ color: 0xd9c08a, metalness: 0.4, roughness: 0.4 }),
      stand: new T.MeshStandardMaterial({ color: 0x3a1e11, metalness: 0.1, roughness: 0.32 }),
      oilLit: new T.MeshStandardMaterial({ color: 0xb98a4a, metalness: 0.75, roughness: 0.32, emissive: 0x7a3a0c, emissiveIntensity: 0.6, side: DOUBLE_SIDE }),
      oilUnlit: new T.MeshStandardMaterial({ color: 0x8a6a48, metalness: 0.7, roughness: 0.45, side: DOUBLE_SIDE }),
      oilSurfaceLit: new T.MeshStandardMaterial({ color: 0xffc46a, roughness: 0.3, emissive: 0xffa040, emissiveIntensity: 1.1, side: DOUBLE_SIDE }),
      oilSurfaceUnlit: new T.MeshStandardMaterial({ color: 0x4a3420, roughness: 0.35, side: DOUBLE_SIDE }),
      // Normal blending so the flame and its warm glow read on the ivory platform.
      flame: new T.MeshBasicMaterial({ map: this.textures.flame, transparent: true, depthWrite: false, toneMapped: false }),
      core: new T.MeshBasicMaterial({ map: this.textures.core, transparent: true, depthWrite: false, toneMapped: false }),
      halo: new T.MeshBasicMaterial({ map: this.textures.halo, transparent: true, depthWrite: false, toneMapped: false, opacity: 0.85 }),
      pool: new T.MeshBasicMaterial({ map: this.textures.pool, transparent: true, depthWrite: false, toneMapped: false, opacity: 0.9 }),
    };
    this.group = new T.Group();
    this.group.name = 'QiyuanGardenLamps';
    this.group.visible = false;
    this.light = new T.PointLight(0xffb067, 0, 1.6, 2);
    this.group.add(this.light);
    api.scene.add(this.group);
    api.onFrame.add(this.frame);

    this.labelLayer = global.document.createElement('div');
    this.labelLayer.className = 'qcg-lamp-label-layer';
    this.labelLayer.setAttribute('aria-hidden', 'true');
    (api.stage || api.canvas.parentNode).appendChild(this.labelLayer);
    this.previewLabel = this.createLabel('qcg-lamp-label qcg-lamp-label--preview');
    this.tapLabel = this.createLabel('qcg-lamp-label');
    api.canvas.addEventListener('pointerdown', this.handlePointerDown);
    api.canvas.addEventListener('pointerup', this.handlePointerUp);
    return true;
  };

  Lamps.prototype.createLabel = function (className) {
    var label = global.document.createElement('div');
    label.className = className;
    label.hidden = true;
    var kicker = global.document.createElement('span');
    var name = global.document.createElement('strong');
    label.appendChild(kicker);
    label.appendChild(name);
    this.labelLayer.appendChild(label);
    return label;
  };

  Lamps.prototype.setLabel = function (label, kicker, name) {
    label.firstChild.textContent = kicker;
    label.lastChild.textContent = name;
  };

  Lamps.prototype.createLamp = function (slot, groundY, state) {
    var T = this.T;
    var lamp = new T.Group();
    var cup = new T.Group();
    var unlit = state === 'unlit';
    var oil = (LAYOUTS[this.scene] || {}).style === 'oil';
    var flameY = 0.86;
    if (oil) {
      cup.add(new T.Mesh(this.geometries.oilBody, unlit ? this.materials.oilUnlit : this.materials.oilLit));
      cup.add(new T.Mesh(this.geometries.oilSurface, unlit ? this.materials.oilSurfaceUnlit : this.materials.oilSurfaceLit));
      flameY = 1.02;
    } else {
      var lotus = new T.Mesh(this.geometries.lotus, unlit ? this.materials.lotusUnlit : this.materials.lotusLit);
      var heart = new T.Mesh(this.geometries.heart, unlit ? this.materials.heartUnlit : this.materials.heartLit);
      lotus.position.y = 0.19;
      heart.position.y = 0.19;
      lotus.rotation.y = Math.random() * Math.PI;
      cup.add(new T.Mesh(this.geometries.stand, this.materials.stand));
      cup.add(lotus);
      cup.add(heart);
      flameY = 0.68;
    }
    lamp.add(cup);
    lamp.position.set(slot[0], groundY, slot[1]);
    lamp.scale.setScalar(LAMP_SCALE * (state === 'preview' ? 1.18 : 1));
    lamp.userData = { state: state, phase: Math.random() * Math.PI * 2, slot: slot };

    if (state !== 'unlit') {
      var flame = new T.Mesh(this.geometries.flame, this.materials.flame);
      flame.position.y = flameY;
      var core = new T.Mesh(this.geometries.core, this.materials.core);
      core.position.set(0, -0.12, 0.01);
      flame.add(core);
      var halo = new T.Mesh(this.geometries.halo, state === 'preview' ? this.materials.halo.clone() : this.materials.halo);
      halo.position.y = flameY - 0.08;
      if (state === 'preview') halo.material.opacity = 0.9;
      var pool = new T.Mesh(this.geometries.pool, this.materials.pool);
      pool.rotation.x = -Math.PI / 2;
      pool.position.y = 0.02;
      lamp.add(pool);
      lamp.add(halo);
      lamp.add(flame);
      lamp.userData.flame = flame;
      lamp.userData.halo = halo;
    }
    this.group.add(lamp);
    this.lamps.push(lamp);
    return lamp;
  };

  Lamps.prototype.clearLamps = function () {
    this.lamps.forEach(function (lamp) {
      if (lamp.userData.halo && lamp.userData.halo.material !== this.materials.halo) lamp.userData.halo.material.dispose();
      this.group.remove(lamp);
    }, this);
    this.lamps = [];
    this.previewLamp = null;
  };

  Lamps.prototype.rebuild = function () {
    if (!this.ensure()) return;
    this.clearLamps();
    var layout = LAYOUTS[this.scene];
    if (!layout || !this.model) {
      this.group.visible = false;
      this.syncLabels();
      return;
    }
    var sample = this.sampler;
    var lights = (this.lightsByScene[this.scene] || []).slice(0, MAX_LIT);
    var showPreview = this.previewDetails && this.previewScene === this.scene;
    var slots = layout.slots;
    var used = 0;
    var litCount = 0;
    var cx = 0;
    var cz = 0;

    if (showPreview && slots.length) {
      this.previewLamp = this.createLamp(slots[0], sample(slots[0][0], slots[0][1]), 'preview');
      used = 1;
    }
    lights.forEach(function (light) {
      if (used >= slots.length) return;
      var slot = slots[used];
      var lamp = this.createLamp(slot, sample(slot[0], slot[1]), 'lit');
      lamp.userData.name = clean(light.display_name) || 'Anonymous';
      used += 1;
    }, this);
    for (; used < slots.length; used += 1) {
      this.createLamp(slots[used], sample(slots[used][0], slots[used][1]), 'unlit');
    }
    this.lamps.forEach(function (lamp) {
      if (lamp.userData.state === 'unlit') return;
      litCount += 1;
      cx += lamp.position.x;
      cz += lamp.position.z;
    });
    if (litCount) {
      this.light.position.set(cx / litCount, sample(cx / litCount, cz / litCount) + 0.14, cz / litCount);
      this.light.intensity = Math.min(0.05 + litCount * 0.018, 0.22);
    } else {
      this.light.intensity = 0;
    }
    this.group.visible = true;
    this.syncLabels();
  };

  Lamps.prototype.showScene = function (scene, model) {
    if (!this.ensure()) return;
    this.scene = scene || '';
    this.model = model || (this.api.getModel && this.api.getModel()) || null;
    this.sampler = this.model ? groundSampler(this.T, this.model) : function () { return 0; };
    this.dressGround(this.model);
    if (LAYOUTS[this.scene]) this.frameHall();
    this.rebuild();
  };

  Lamps.prototype.groundStyle = function () {
    var root = this.viewerElement && this.viewerElement.closest ? this.viewerElement.closest('[data-qy-garden]') : null;
    return (root && root.getAttribute('data-qy-ground')) || 'bluestone';
  };

  // Replace the ivory platform with bluestone (青石) paving: planar UVs in world space + generated textures.
  Lamps.prototype.dressGround = function (model) {
    if (!model || this.groundStyle() !== 'bluestone') return;
    var T = this.T;
    var mesh = groundMesh(T, model);
    if (!mesh || !mesh.geometry || mesh.userData.qyBluestone) return;
    if (!this.stone) this.stone = bluestoneTextures(T, this.api.renderer);
    mesh.updateWorldMatrix(true, false);
    var geometry = mesh.geometry.clone();
    var position = geometry.attributes.position;
    var uv = [];
    var v = new T.Vector3();
    for (var i = 0; i < position.count; i += 1) {
      v.set(position.getX(i), position.getY(i), position.getZ(i)).applyMatrix4(mesh.matrixWorld);
      uv.push(v.x / STONE_TILE, v.z / STONE_TILE);
    }
    geometry.setAttribute('uv', new T.Float32BufferAttribute(uv, 2));
    mesh.geometry = geometry;
    var material = new T.MeshStandardMaterial({
      map: this.stone.map,
      normalMap: this.stone.normalMap,
      roughness: 0.8,
      metalness: 0,
      side: DOUBLE_SIDE,
    });
    material.normalScale.set(0.8, 0.8);
    material.envMapIntensity = 0.5;
    mesh.material = material;
    mesh.userData.qyBluestone = true;
  };

  // Raise the hall camera a little so the rows of lamps read clearly.
  Lamps.prototype.frameHall = function () {
    var camera = this.api.camera;
    var controls = this.api.controls;
    var distance = camera.position.distanceTo(controls.target);
    camera.position.set(
      controls.target.x,
      controls.target.y + distance * Math.sin(VIEW_ELEVATION),
      controls.target.z + distance * Math.cos(VIEW_ELEVATION)
    );
    controls.update();
  };

  Lamps.prototype.hide = function () {
    if (!this.group) return;
    this.group.visible = false;
    this.model = null;
    this.scene = '';
    this.syncLabels();
  };

  Lamps.prototype.setLights = function (scene, lights) {
    this.lightsByScene[scene] = Array.isArray(lights) ? lights.slice() : [];
    if (scene === this.scene) this.rebuild();
  };

  Lamps.prototype.preview = function (scene, details) {
    this.previewScene = scene;
    this.previewDetails = details || {};
    if (this.previewLabel) {
      this.setLabel(this.previewLabel, clean(this.previewDetails.kicker) || 'Your light · preview', clean(this.previewDetails.name) || 'Anonymous');
    }
    if (scene === this.scene) this.rebuild();
  };

  Lamps.prototype.clearPreview = function () {
    var had = Boolean(this.previewDetails);
    this.previewDetails = null;
    this.previewScene = '';
    if (had && this.scene) this.rebuild();
  };

  Lamps.prototype.screenPoint = function (lamp) {
    var T = this.T;
    var api = this.api;
    var v = new T.Vector3(0, 1.25, 0);
    lamp.localToWorld(v);
    v.project(api.camera);
    var rect = api.canvas.getBoundingClientRect();
    var host = this.labelLayer.getBoundingClientRect();
    return {
      x: (v.x + 1) / 2 * rect.width + (rect.left - host.left),
      y: (1 - v.y) / 2 * rect.height + (rect.top - host.top),
      visible: v.z < 1 && v.x > -1.1 && v.x < 1.1 && v.y > -1.1 && v.y < 1.1,
    };
  };

  Lamps.prototype.placeLabel = function (label, lamp) {
    if (!lamp || !this.group || !this.group.visible) {
      label.hidden = true;
      return;
    }
    var point = this.screenPoint(lamp);
    label.hidden = !point.visible;
    label.style.transform = 'translate(' + point.x.toFixed(1) + 'px,' + point.y.toFixed(1) + 'px) translate(-50%, calc(-100% - 4px))';
  };

  Lamps.prototype.syncLabels = function () {
    if (!this.labelLayer) return;
    this.placeLabel(this.previewLabel, this.previewLamp);
    this.placeLabel(this.tapLabel, this.tapLamp && this.lamps.indexOf(this.tapLamp) !== -1 ? this.tapLamp : null);
  };

  Lamps.prototype.frame = function () {
    if (!this.group || !this.group.visible) return;
    var camera = this.api.camera;
    var time = global.performance ? global.performance.now() / 1000 : Date.now() / 1000;
    var still = reducedMotion();
    this.lamps.forEach(function (lamp) {
      var data = lamp.userData;
      if (!data.flame) return;
      var yaw = Math.atan2(camera.position.x - lamp.position.x, camera.position.z - lamp.position.z);
      data.flame.rotation.set(0, yaw, 0);
      data.halo.quaternion.copy(camera.quaternion);
      if (still) return;
      var flicker = 1 + 0.07 * Math.sin(time * 7.3 + data.phase) + 0.04 * Math.sin(time * 12.1 + data.phase * 1.7);
      data.flame.scale.set(1, flicker, 1);
      data.halo.scale.setScalar(0.96 + 0.05 * Math.sin(time * 2.1 + data.phase));
    });
    this.syncLabels();
  };

  Lamps.prototype.handlePointerDown = function (event) {
    this.pointerStart = { x: event.clientX, y: event.clientY, time: Date.now() };
  };

  Lamps.prototype.handlePointerUp = function (event) {
    var start = this.pointerStart;
    this.pointerStart = null;
    if (!start || !this.group || !this.group.visible) return;
    if (Math.abs(event.clientX - start.x) > 6 || Math.abs(event.clientY - start.y) > 6 || Date.now() - start.time > 500) return;
    var host = this.labelLayer.getBoundingClientRect();
    var x = event.clientX - host.left;
    var y = event.clientY - host.top;
    var nearest = null;
    var nearestDistance = 30;
    this.lamps.forEach(function (lamp) {
      if (lamp.userData.state !== 'lit') return;
      var point = this.screenPoint(lamp);
      var distance = Math.hypot(point.x - x, point.y - y - 10);
      if (point.visible && distance < nearestDistance) {
        nearest = lamp;
        nearestDistance = distance;
      }
    }, this);
    if (!nearest) return;
    this.tapLamp = nearest;
    this.setLabel(this.tapLabel, 'A light for', nearest.userData.name || 'Anonymous');
    this.syncLabels();
    global.clearTimeout(this.tapTimer);
    this.tapTimer = global.setTimeout(function () {
      this.tapLamp = null;
      this.syncLabels();
    }.bind(this), 4000);
  };

  global.QiyuanGardenLamps = {
    version: '1.0.0',
    layouts: LAYOUTS,
    attach: function (viewerElement, options) {
      return new Lamps(viewerElement, options);
    },
  };
})(window);
